import { StoreWritable, createStore, createEvent } from 'effector'
import { callWsService } from './call-ws.service'

export type ServiceError = {
  reason: string
}

class CallServiceErrorService {
  readonly $serviceError: StoreWritable<ServiceError | null> =
    createStore<ServiceError | null>(null)

  readonly setServiceError = createEvent<ServiceError>()

  // test-only escape hatch: $serviceError is a module-level singleton with
  // no reset in production (a fatal error must persist for the rest of the
  // session), so tests need a way to clear it between cases.
  readonly resetForTests = createEvent()

  // debug-only, UI-side override for manually verifying the fatal overlay on
  // a live environment without touching real interview data. Deliberately a
  // separate store from $serviceError: the overlay only renders off the
  // persisted interview status (see interview-router.ui.tsx), and this must
  // never let a real (or emulated) websocket message alone flip the UI.
  readonly $debugForceOverlay: StoreWritable<boolean> =
    createStore<boolean>(false)
  readonly debugForceOverlay = createEvent()

  constructor() {
    this.$serviceError.on(this.setServiceError, (_, error) => error)
    this.$serviceError.reset(this.resetForTests)
    this.$debugForceOverlay.on(this.debugForceOverlay, () => true)
    this.$debugForceOverlay.reset(this.resetForTests)

    this.setServiceError.watch(() => {
      // stop the WS reconnect loop - a fatal service error must never be
      // silently retried in the background
      callWsService.setShouldReconnect(false)
      callWsService.doCloseConnection()
    })

    if (typeof window !== 'undefined') {
      window.emulateServiceError = (reason?: string) => {
        console.warn(
          '[DEBUG] emulateServiceError: dispatching a real service_error_message ' +
            'through the normal handler (stops WS reconnect, closes the socket, ' +
            'forces a refetch of the persisted interview status). The fatal overlay ' +
            'will NOT appear unless the backend has actually persisted service_error - ' +
            'use window.forceServiceErrorOverlay() to just preview the overlay UI.'
        )
        this.setServiceError({
          reason: reason ?? 'DEBUG: emulated service error'
        })
      }

      window.forceServiceErrorOverlay = () => {
        console.warn(
          '[DEBUG] forceServiceErrorOverlay: UI-only override, no backend/websocket ' +
            'involved - just previews what the fatal overlay looks like.'
        )
        this.debugForceOverlay()
      }
    }
  }

  subscribe = (): VoidFunction => {
    return callWsService.autoSubscribeToWsMsgOnOpen(
      'call-service-error-service',
      (msg) => {
        const { serviceErrorMessage } = msg
        if (!serviceErrorMessage) return

        this.setServiceError({ reason: serviceErrorMessage.reason })
      }
    )
  }
}

export const callServiceErrorService = new CallServiceErrorService()
